import PreviousRequestsSection from "./TabTermination/PreviousRequestsSection"
import TabTerminationDetailsDisplay from "./TabTermination/ActiveTerminationSection/DetailsDisplay"
import TerminationRequest from "./TabTermination/TerminationRequest"
import GeneralTerminationDetails from "./TabTermination/GeneralTerminationDetails"
import GeneralTerminationDetailsForm from "./TabTermination/GeneralTerminationDetailsForm"
import PaymentDetails from "./TabTermination/PaymentDetails"
import AdditionalDetails from "./TabTermination/AdditionalDetails"
import ActionsFooter from "./TabTermination/ActiveTerminationSection/ActionsFooter"
import InvoicePaymentsDisplay from "./TabTermination/ActiveTerminationSection/InvoicePaymentsDisplay"
import {
  Engagement,
  TerminationState,
  Employment,
  EngagementType,
  Termination,
} from "@oysterhr/schema"
import { useQuery } from "@apollo/client"
import { useTranslation } from "react-i18next"
import { staffOffboardingFlowPath } from "@oysterhr/router"
import { Button, Heading, Alert, LoadingSpinner } from "@oysterhr/ui"
import { DateTime } from "luxon"
import EditableSection from "~/adminApp/pages/EmploymentPage/TabShared/EditableSection"
import Section from "~/adminApp/pages/EmploymentPage/TabShared/Section"
import { useNavigate } from "react-router-dom"
import { useAuthorized } from "@oysterhr/auth"
import { useRunTerminationCompletionAction } from "@oysterhr/offboarding"
import { gql } from "../gql"
import { useTerminationFacts } from "../hooks/useTerminationFacts"
import { DisabledOffboardingFlowAlert } from "./Alerts/DisabledOffboardingFlowAlert"

const ContractEmptyState = () => (
  <div className="py-8">
    No termination request for this contract. Once the company starts a new termination process it
    will be displayed here.
  </div>
)

interface TabTerminationProps {
  engagementId: string
}

export const TERMINATION_FRAGMENT = gql(`
  fragment TerminationFragment on Termination {
    approver {
      avatarUrl
      id
      name
    }
    cancelReason
    cancelledAt
    cancelledBy {
      avatarUrl
      id
      name
    }
    initiatedAt
    id
    lastPayment
    lastPaymentAmount
    lastPaymentCurrencyCode
    note
    onLeave
    proposedDate
    bulkOffboardingStatusDraft
    reason
    requestor {
      avatarUrl
      id
      isOysterStaff
      name
      type
    }
    offboardingSpecialist {
      id
      name
      avatarUrl
      email
    }
    state
    primaryCategory {
      id
      name
    }
    secondaryCategory {
      id
      name
    }
    communicatedWithEmployee
    intendedDaysOff
    notes
    ptoInaccurateNote
    documents {
      id
      title
      date
      tags
      filename
      createdAt
      description
    }
    additionalTerminationPayments {
      id
      amount
      currency
      amountAsPercentage
      paymentFrequency
      details
      numRecurringPayments
      offboardingPayType {
        id
        name
      }
    }
    paidInFullCheckDate
    paidInFullStatus
    ptoInfo {
      daysRemaining
      daysTaken
      daysApproved
      leaveYear
      timeOffManagementEnabled
    }
    mutualTermination
    serviceAgreementSignature
    tosAgreementGenerated
    additionalNotesForDepositTeam
    staffDepositNote
    additionalConsiderationAmount {
      subunit
      subunitToUnit
      currencyCode
    }
    integrationName
    additionalConsiderationAmountToBeConfirmed
    authorizations {
      actions
    }
    shouldRunCompletionActions
  }
`)

export const STAFF_TERMINATION_QUERY_NAME = "StaffEmploymentPageTermination"

const TERMINATION_TAB_QUERY = gql(`
  query StaffEmploymentPageTermination($engagementId: ID!) {
    employment(employmentId: $engagementId) {
      id
      lastDayOn
      lastWorkingDay
      name
      salaryCurrencyCode
      restrictiveCovenants {
        confidentiality
      }
      probationEnd
      serviceAgreementSignedEventDate
      severance {
        amount {
          subunit
          subunitToUnit
          currencyCode
        }
        severanceType
        period
        periodDuration
      }
      notice {
        amount {
          subunit
          subunitToUnit
          currencyCode
        }
        entitlement
        noticeType
        period
        periodDuration
        gardenLeave
      }
      company {
        id
        depositInvoiceRequired
      }
      hiring {
        agreements {
          agreementType
          envelope {
            state
            events {
              eventType
              createdAt
            }
          }
        }
      }
    }
    engagement(engagementId: $engagementId) {
      id
      name
      state
      engagementType
      payrollCurrencyCode
      isPayrollTeamMember
      pendingEngagementChange {
        compensationChanged
      }
      company {
        id
        countryCode
      }
      cancelledTerminations {
        ...TerminationFragment
      }

      termination {
        ...TerminationFragment
      }
      terminationDraft {
        id
        state
        createdAt
        proposedDate
        integrationSyncOn
        integrationName
        bulkOffboardingStatusDraft
        bulkOffboarding {
          id
        }
      }
    }
  }
`)

export const TabTermination = ({ engagementId }: TabTerminationProps) => {
  const { t } = useTranslation("staff_interface/TabTermination")
  const { loading, data, refetch } = useQuery(TERMINATION_TAB_QUERY, {
    variables: {
      engagementId,
    },
  })
  const navigate = useNavigate()

  const engagement = data?.engagement as Engagement
  const employment = data?.employment as Employment

  const latestTermination = engagement?.termination as Termination
  const previousTerminations = engagement?.cancelledTerminations || []

  const { isAuthorized } = useAuthorized(latestTermination || { authorizations: { actions: [] } })
  const authorizedToUpdateTerminated = isAuthorized(["update_terminated"])

  const isContract = engagement?.engagementType === EngagementType.Contract
  const isEmployment = engagement?.engagementType === EngagementType.Employment
  const nonEditableStates: (TerminationState | undefined)[] = [
    TerminationState.Approved,
    TerminationState.Terminated,
  ]
  const isEditable =
    !nonEditableStates.includes(latestTermination?.state) || authorizedToUpdateTerminated
  const terminationDateInFuture =
    !employment?.lastDayOn ||
    DateTime.fromISO(employment?.lastDayOn).toISODate() >= DateTime.now().toISODate()
  const effectiveTerminationDetailsIsEditable =
    (terminationDateInFuture && !latestTermination?.tosAgreementGenerated) ||
    authorizedToUpdateTerminated

  const [runCompletionActions, runCompletionData] = useRunTerminationCompletionAction({
    refetchQueries: ["StaffEmploymentPageTermination"],
    awaitRefetchQueries: true,
  })

  const onMarkAsCompleted = async () => {
    if (runCompletionData.loading) {
      return
    }

    runCompletionActions({
      variables: {
        terminationId: latestTermination?.id,
      },
    })
  }

  const { terminationIncludedInDraftBulk, draftBulkOffboarding } = useTerminationFacts(engagement)

  if (loading || !data) {
    return (
      <div className="mt-4 text-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (!employment) {
    return (
      <div className="mt-8">
        <p>Employment not found</p>
      </div>
    )
  }

  if (!engagement) {
    return (
      <div className="mt-8">
        <p>Engagement not found</p>
      </div>
    )
  }

  const naviateToOffboardingFlow = () => {
    navigate(staffOffboardingFlowPath(engagement.company.id, engagement.id))
  }

  return (
    <div
      className="mx-8 mb-24 mt-8"
      style={{ width: "44rem" }}
      data-testid="employment-termination-details"
    >
      {isEmployment && engagement?.pendingEngagementChange?.compensationChanged && (
        <Alert className="mb-8 mt-2" variation="warning">
          <Alert.Icon />
          <Alert.Content>{t("contractChangeDisclaimer")}</Alert.Content>
        </Alert>
      )}
      {latestTermination?.shouldRunCompletionActions && !isContract && (
        <div>
          <Alert className="mb-8 mt-2" variation="warning">
            <Alert.Icon />
            <Alert.Content>
              {t("markAsCompletedFTE.modal.title")}
              <ul className="list-disc">
                <li>{t("markAsCompletedFTE.modal.agreementWillRegenerate")}</li>
                <li>{t("markAsCompletedFTE.modal.perlWillRegenerate")}</li>
                <li>{t("markAsCompletedFTE.modal.zendeskTicketsWillUpdate")}</li>
              </ul>
            </Alert.Content>
          </Alert>
          <Button onClick={onMarkAsCompleted} isFullWidth>
            {t("markAsCompletedFTE.button")}
          </Button>
        </div>
      )}
      <div className="mb-10">
        {!latestTermination && (
          <>
            {isContract ? (
              <ContractEmptyState />
            ) : (
              (!engagement.termination || engagement.terminationDraft) && (
                <div>
                  <Button
                    onClick={naviateToOffboardingFlow}
                    disabled={terminationIncludedInDraftBulk}
                    isFullWidth
                  >
                    {engagement.terminationDraft ? t("continue") : t("start")}
                  </Button>
                  {terminationIncludedInDraftBulk && draftBulkOffboarding && (
                    <DisabledOffboardingFlowAlert bulkOffboardingId={draftBulkOffboarding?.id} />
                  )}
                </div>
              )
            )}
            {engagement.terminationDraft &&
              engagement.terminationDraft.integrationSyncOn === true && (
                <Alert className="mt-4" variation="warning">
                  <Alert.Icon />
                  <Alert.Content>
                    {t("integrationSyncWarning", {
                      integrationName: engagement.terminationDraft.integrationName,
                    })}
                  </Alert.Content>
                </Alert>
              )}
          </>
        )}
        {latestTermination && (
          <div data-testid="current-termination-request">
            <div data-testid="general-details">
              <EditableSection title={t("generalSectionTitle")} isEditable={isEditable}>
                {({ isEditing, setEditing }) =>
                  isEditing ? (
                    <GeneralTerminationDetailsForm
                      termination={latestTermination}
                      setEditing={setEditing}
                    />
                  ) : (
                    <GeneralTerminationDetails termination={latestTermination} />
                  )
                }
              </EditableSection>
            </div>
            <Section title={t("paymentSectionTitle")}>
              <PaymentDetails
                engagement={engagement}
                termination={latestTermination}
                isEditable={isEditable}
              />
            </Section>
            <Section title={t("detailsSectionTitle")}>
              <AdditionalDetails termination={latestTermination} />
            </Section>
            <Section title={t("terminationRequestDetailsTitle")}>
              <TerminationRequest termination={latestTermination} noStatus />
            </Section>
            <div data-testid="effective-termination-details">
              <EditableSection
                title={t("effectiveTerminationDetailsTitle")}
                isEditable={effectiveTerminationDetailsIsEditable}
              >
                {({ isEditing, setEditing }) => (
                  <TabTerminationDetailsDisplay
                    employment={employment}
                    termination={latestTermination}
                    setEditing={setEditing}
                    isEditing={isEditing}
                  />
                )}
              </EditableSection>
            </div>
            <InvoicePaymentsDisplay
              termination={latestTermination}
              company={engagement.company}
              lastDayOn={employment.lastDayOn}
              refetch={refetch}
            />
            <ActionsFooter
              refetch={refetch}
              latestTermination={latestTermination}
              engagement={engagement}
              employment={employment}
              isContract={isContract}
            />
          </div>
        )}
      </div>

      {!!previousTerminations.length && (
        <>
          <Heading as="h3" className="mb-3">
            Previous Termination Requests
          </Heading>
          <PreviousRequestsSection terminations={previousTerminations} />
        </>
      )}
    </div>
  )
}
